import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AuthContext from "../context/AuthContext";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { useForm, Controller } from "react-hook-form";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

let helcimScriptPromise = null;
const HELCIM_SCRIPT_DATA_ATTR = "data-helcim-payjs";

const getHelcimGlobal = () =>
  typeof window === "undefined"
    ? undefined
    : window.HelcimPay ||
      window.helcimPay ||
      window.Helcim ||
      window.HelcimPayJS ||
      (window.appendHelcimPayIframe && window.removeHelcimPayIframe
        ? window
        : undefined);

const cleanupHelcimScriptTag = () => {
  if (typeof document === "undefined") return;
  const existingScript = document.querySelector(
    `script[${HELCIM_SCRIPT_DATA_ATTR}]`
  );
  if (existingScript) {
    existingScript.remove();
  }
};

const waitForHelcimGlobal = (maxAttempts = 30, delayMs = 100) =>
  new Promise((resolve, reject) => {
    let attempts = 0;
    const check = () => {
      const helcim = getHelcimGlobal();
      if (helcim) {
        resolve(helcim);
        return;
      }
      attempts += 1;
      if (attempts >= maxAttempts) {
        const availableGlobals = Object.keys(window)
          .filter((key) => key.toLowerCase().includes("helcim"))
          .join(", ");
        const errorMsg = `HelcimPay.js loaded but did not expose the expected global object. Available Helcim-related globals: ${
          availableGlobals || "none"
        }`;
        console.error(errorMsg);
        reject(new Error(errorMsg));
        return;
      }
      setTimeout(check, delayMs);
    };
    check();
  });

const RedSpan = styled("span")(({ theme }) => ({
  color: "red",
}));

const bypassFreemium = process.env.REACT_APP_BYPASS_FREEMIUM === "true";

const Signup = () => {
  const { signup, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const apiBaseUrl = `${process.env.REACT_APP_API_BASE_URL}/api/v3`;
  const {
    handleSubmit,
    control,
    register,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      plan: "free",
      displayName: "",
      billing: {
        fullName: "",
        helcim: {
          customerId: "",
          defaultCardToken: "",
        },
        cardToken: "",
        address: {
          line1: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
        },
      },
    },
  });

  const planValue = watch("plan");
  const isPaidPlan = planValue !== "free";
  const trialConfig = useMemo(() => {
    const enabled =
      String(process.env.REACT_APP_TRIAL_ENABLED || "").toLowerCase() ===
      "true";
    const includeFree =
      String(process.env.REACT_APP_TRIAL_INCLUDE_FREE || "").toLowerCase() ===
      "true";
    const lengthRaw = Number(process.env.REACT_APP_TRIAL_LENGTH_DAYS || 0);
    return {
      enabled,
      includeFree,
      lengthDays: Number.isFinite(lengthRaw) ? lengthRaw : 0,
    };
  }, []);
  const isTrialPlan =
    trialConfig.enabled &&
    trialConfig.lengthDays > 0 &&
    (planValue !== "free" || trialConfig.includeFree);
  const shouldChargeForPlan = isPaidPlan && !isTrialPlan;

  const [helcimCheckoutToken, setHelcimCheckoutToken] = useState("");
  const [helcimInitStatus, setHelcimInitStatus] = useState("idle");
  const [helcimInitError, setHelcimInitError] = useState("");
  const helcimPayJsUrl =
    process.env.REACT_APP_HELCIM_PAYJS_URL ||
    "https://secure.helcim.app/helcim-pay/services/start.js";
  const [helcimScriptStatus, setHelcimScriptStatus] = useState(
    typeof window !== "undefined" && !!getHelcimGlobal() ? "ready" : "idle"
  );
  const [helcimScriptError, setHelcimScriptError] = useState("");
  const [helcimTokenMessage, setHelcimTokenMessage] = useState("");
  const [isConfirmingHelcim, setIsConfirmingHelcim] = useState(false);

  const scrollRestoreRef = useRef(null);

  const helcimPlanAmount = useMemo(() => {
    const parseAmount = (value, fallback) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    const defaultAmount = parseAmount(
      process.env.REACT_APP_HELCIM_DEFAULT_AMOUNT,
      0
    );
    const planAmounts = {
      pro_monthly: parseAmount(
        process.env.REACT_APP_HELCIM_PRO_MONTHLY_AMOUNT,
        5
      ),
      pro_annual: parseAmount(
        process.env.REACT_APP_HELCIM_PRO_ANNUAL_AMOUNT,
        50
      ),
    };
    return planAmounts[planValue] ?? defaultAmount;
  }, [planValue]);

  const helcimScriptReady = helcimScriptStatus === "ready";
  const helcimActionDisabled =
    helcimInitStatus !== "ready" ||
    !helcimCheckoutToken ||
    !helcimScriptReady ||
    isConfirmingHelcim;
  const subscribeDisabled = helcimActionDisabled || !shouldChargeForPlan;
  const trialBillingMessage = useMemo(() => {
    if (!isTrialPlan) return "";
    const lengthDescriptor =
      trialConfig.lengthDays > 0 ? `${trialConfig.lengthDays}-day` : "trial";
    return `A ${lengthDescriptor} trial is active. Your payment method will be saved without charging until the trial ends.`;
  }, [isTrialPlan, trialConfig.lengthDays]);

  const refreshHelcimCheckoutToken = useCallback(
    async (isPurchase = true) => {
      setHelcimInitStatus("loading");
      setHelcimInitError("");
      setHelcimTokenMessage("");
      try {
        const paymentType = isPurchase ? "purchase" : "verify";
        const amount = isPurchase ? helcimPlanAmount : 0;

        const res = await fetch(`${apiBaseUrl}/helcim/initialize`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paymentType: paymentType,
            amount: amount,
            currency: process.env.REACT_APP_HELCIM_CURRENCY || "USD",
            paymentMethod: "cc",
          }),
        });
        if (!res.ok) {
          throw new Error(`Helcim initialize failed (${res.status})`);
        }
        const data = await res.json();
        const checkoutToken =
          data.checkoutToken ||
          data?.data?.checkoutToken ||
          data?.data?.data?.checkoutToken;
        if (!checkoutToken) {
          throw new Error("Helcim initialize did not return a checkout token");
        }
        setHelcimCheckoutToken(checkoutToken);
        setHelcimInitStatus("ready");
        return checkoutToken;
      } catch (error) {
        setHelcimInitStatus("error");
        setHelcimInitError(error.message || "Helcim initialization failed");
        throw error;
      }
    },
    [apiBaseUrl, helcimPlanAmount]
  );

  const loadHelcimScript = useCallback(() => {
    if (typeof window === "undefined") {
      return Promise.reject(new Error("Window is not available"));
    }

    const helcimGlobal = getHelcimGlobal();

    if (!helcimGlobal && helcimScriptPromise) {
      cleanupHelcimScriptTag();
      helcimScriptPromise = null;
    }

    if (helcimGlobal && helcimScriptPromise) {
      setHelcimScriptStatus("ready");
      setHelcimScriptError("");
      return helcimScriptPromise;
    }

    if (helcimScriptPromise) {
      return helcimScriptPromise;
    }

    const existingScript = document.querySelector(
      `script[${HELCIM_SCRIPT_DATA_ATTR}]`
    );

    if (existingScript) {
      const status = existingScript.getAttribute("data-status");

      if (status === "ready") {
        const helcim = getHelcimGlobal();
        if (!helcim) {
          cleanupHelcimScriptTag();
        } else {
          const resolved = Promise.resolve(helcim);
          helcimScriptPromise = resolved;
          setHelcimScriptStatus("ready");
          setHelcimScriptError("");
          return resolved;
        }
      }

      if (status === "loading") {
        helcimScriptPromise = new Promise((resolve, reject) => {
          existingScript.addEventListener("load", () =>
            resolve(getHelcimGlobal())
          );
          existingScript.addEventListener("error", () =>
            reject(new Error("HelcimPay.js failed to load"))
          );
        });
        return helcimScriptPromise;
      }

      if (status === "error") {
        cleanupHelcimScriptTag();
      }
    }

    helcimScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = helcimPayJsUrl;
      script.async = true;
      script.defer = true;
      script.setAttribute(HELCIM_SCRIPT_DATA_ATTR, "true");
      script.setAttribute("data-status", "loading");
      script.onload = () => {
        waitForHelcimGlobal()
          .then((helcim) => {
            script.setAttribute("data-status", "ready");
            setHelcimScriptStatus("ready");
            setHelcimScriptError("");
            resolve(helcim);
          })
          .catch((error) => {
            script.setAttribute("data-status", "error");
            setHelcimScriptStatus("error");
            setHelcimScriptError(error.message);
            cleanupHelcimScriptTag();
            helcimScriptPromise = null;
            reject(error);
          });
      };
      script.onerror = () => {
        script.setAttribute("data-status", "error");
        const message =
          "HelcimPay.js failed to load. Set REACT_APP_HELCIM_PAYJS_URL to a reachable CDN.";
        setHelcimScriptStatus("error");
        setHelcimScriptError(message);
        cleanupHelcimScriptTag();
        helcimScriptPromise = null;
        reject(new Error(message));
      };
      document.body.appendChild(script);
      setHelcimScriptStatus("loading");
    });

    return helcimScriptPromise;
  }, [helcimPayJsUrl]);

  useEffect(() => {
    if (!isPaidPlan) {
      setHelcimScriptStatus("idle");
      setHelcimScriptError("");
      return;
    }
    loadHelcimScript().catch((error) => {
      setHelcimScriptStatus("error");
      setHelcimScriptError(error.message);
    });
  }, [isPaidPlan, loadHelcimScript]);

  useEffect(() => {
    if (!isPaidPlan) {
      setHelcimCheckoutToken("");
      setHelcimInitStatus("idle");
      setHelcimInitError("");
      setHelcimTokenMessage("");
      return;
    }
    refreshHelcimCheckoutToken(shouldChargeForPlan).catch(() => {});
  }, [isPaidPlan, refreshHelcimCheckoutToken, shouldChargeForPlan]);

  const normalizeCountryIso2 = (value) => {
    if (!value) return "";
    const v = String(value).trim().toUpperCase();

    // already ISO2
    if (v === "US" || v === "CA") return v;

    // common variants
    if (
      v === "USA" ||
      v === "UNITED STATES" ||
      v === "UNITED STATES OF AMERICA" ||
      v === "U.S." ||
      v === "U.S.A."
    )
      return "US";
    if (v === "CAN" || v === "CANADA") return "CA";

    // optional numeric ISO
    if (v === "840") return "US";
    if (v === "124") return "CA";

    // If you truly only support US+CA, fail closed:
    return "";
  };

  const confirmHelcimToken = async (checkoutToken, tokenPayload) => {
    const normalizeSources = (...payloads) => {
      const seen = new Set();
      const stack = [];
      payloads.forEach((payload) => {
        if (payload && typeof payload === "object" && !seen.has(payload)) {
          seen.add(payload);
          stack.push(payload);
          if (payload.data && typeof payload.data === "object") {
            stack.push(payload.data);
          }
          if (
            payload.paymentProfile &&
            typeof payload.paymentProfile === "object"
          ) {
            stack.push(payload.paymentProfile);
          }
          if (payload.profile && typeof payload.profile === "object") {
            stack.push(payload.profile);
          }
          if (payload.customer && typeof payload.customer === "object") {
            stack.push(payload.customer);
          }
          if (
            payload.billingAddress &&
            typeof payload.billingAddress === "object"
          ) {
            stack.push(payload.billingAddress);
          }
          if (payload.address && typeof payload.address === "object") {
            stack.push(payload.address);
          }
        }
      });
      return stack;
    };

    const sources = normalizeSources(
      tokenPayload?.data,
      tokenPayload,
      tokenPayload?.data?.data,
      tokenPayload?.data?.paymentProfile,
      tokenPayload?.data?.customer,
      tokenPayload?.data?.billingAddress
    );

    const applyBillingValue = (fieldName, value) => {
      if (!value) return;
      setValue(fieldName, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };

    const findValue = (keys) => {
      for (const source of sources) {
        for (const key of keys) {
          if (source?.[key]) {
            return source[key];
          }
          if (source?.billingAddress?.[key]) {
            return source.billingAddress[key];
          }
          if (source?.address?.[key]) {
            return source.address[key];
          }
        }
      }
      return undefined;
    };

    const confirmationBody = {
      checkoutToken,
      ...tokenPayload,
    };
    const res = await fetch(`${apiBaseUrl}/helcim/confirm-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(confirmationBody),
    });
    if (!res.ok) {
      throw new Error(`Helcim token confirmation failed (${res.status})`);
    }
    const data = await res.json();
    sources.push(
      data?.data?.data,
      data?.data,
      data,
      data?.data?.paymentProfile,
      data?.paymentProfile,
      data?.data?.customer,
      data?.customer,
      data?.data?.billingAddress,
      data?.billingAddress,
      data?.data?.address,
      data?.address
    );

    const cardToken = findValue(["cardToken", "token"]);
    const defaultCardToken = findValue(["defaultCardToken"]) || cardToken;
    const customerId = findValue(["customerId", "customerCode"]);
    const fullName = findValue([
      "fullName",
      "name",
      "contactName",
      "billingName",
      "billingContactName",
    ]);
    const line1 = findValue([
      "line1",
      "address1",
      "addressLine1",
      "billingAddress1",
      "street",
      "street1",
      "streetAddress",
      "streetAddress1",
      "address",
    ]);
    const city = findValue(["city"]);
    const state = findValue(["state", "province"]);
    const postalCode = findValue(["postalCode", "zip"]);
    const countryRaw = findValue(["country", "countryCode"]);
    const country = normalizeCountryIso2(countryRaw);

    applyBillingValue("billing.cardToken", cardToken);
    applyBillingValue("billing.helcim.defaultCardToken", defaultCardToken);
    applyBillingValue("billing.helcim.customerId", customerId);
    applyBillingValue("billing.fullName", fullName);
    applyBillingValue("billing.address.line1", line1);
    applyBillingValue("billing.address.city", city);
    applyBillingValue("billing.address.state", state);
    applyBillingValue("billing.address.postalCode", postalCode);
    applyBillingValue("billing.address.country", country);

    if (cardToken || defaultCardToken || customerId) {
      setHelcimTokenMessage("Helcim token secured and copied into the form.");
    }
    return data;
  };

  const closeHelcimModal = () => {
    // Restore scroll (in case Helcim or your code modified it)
    try {
      if (scrollRestoreRef.current) {
        const { htmlOverflow, bodyOverflow } = scrollRestoreRef.current;
        document.documentElement.style.overflow = htmlOverflow;
        document.body.style.overflow = bodyOverflow;
        scrollRestoreRef.current = null;
      } else {
        // safe fallback
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
      }
    } catch (_) {}

    // Remove iframe (prefer Helcim's function if present, otherwise remove by id)
    try {
      const helcim = getHelcimGlobal();
      if (helcim && typeof helcim.removeHelcimPayIframe === "function") {
        helcim.removeHelcimPayIframe();
        return;
      }
      if (typeof window.removeHelcimPayIframe === "function") {
        window.removeHelcimPayIframe();
        return;
      }
    } catch (_) {}

    const frame = document.getElementById("helcimPayIframe");
    if (frame) frame.remove();
  };

  const handleHelcimFlow = async (shouldPurchase = true) => {
    setHelcimTokenMessage("");
    setHelcimInitError("");
    setIsConfirmingHelcim(true);
    try {
      const checkoutToken = await refreshHelcimCheckoutToken(shouldPurchase);
      await loadHelcimScript();
      if (typeof window.appendHelcimPayIframe !== "function") {
        throw new Error(
          "HelcimPay.js does not expose appendHelcimPayIframe function"
        );
      }

      const eventName = `helcim-pay-js-${checkoutToken}`;
      const expectedOrigin = new URL(helcimPayJsUrl).origin;
      const removeListener = () => {
        window.removeEventListener("message", handleMessage);
      };

      const handleMessage = async (event) => {
        if (event.origin !== expectedOrigin) {
          return;
        }

        if (event?.data?.eventName !== eventName) {
          return;
        }

        if (event.data.eventStatus === "ABORTED") {
          removeListener();
          closeHelcimModal();
          setIsConfirmingHelcim(false);
          setHelcimInitError("Helcim payment aborted");
          toast.error("Helcim payment aborted");
          return;
        }

        if (event.data.eventStatus === "HIDE") {
          removeListener();
          closeHelcimModal();
          setIsConfirmingHelcim(false);
          return;
        }

        if (event.data.eventStatus !== "SUCCESS") {
          return;
        }

        removeListener();

        try {
          const parsedMessage =
            typeof event.data.eventMessage === "string"
              ? JSON.parse(event.data.eventMessage)
              : event.data.eventMessage;
          const rawDataResponse = parsedMessage?.data?.data;
          const helcimHash = parsedMessage?.data?.hash;

          await confirmHelcimToken(checkoutToken, {
            data: rawDataResponse,
            hash: helcimHash,
          });
          toast.success("Helcim token confirmed");
        } catch (error) {
          const message = error?.message || "Helcim tokenization failed";
          setHelcimInitError(message);
          toast.error(message);
        } finally {
          closeHelcimModal();
          setIsConfirmingHelcim(false);
        }
      };

      window.addEventListener("message", handleMessage);
        window.appendHelcimPayIframe(checkoutToken, true);
    } catch (error) {
      const message = error?.message || "Helcim tokenization failed";
      setHelcimInitError(message);
      toast.error(message);
      setIsConfirmingHelcim(false);
    }
  };

  const onSubmit = async (formValues) => {
    const { gateway: _gateway, ...billingWithoutGateway } =
      formValues.billing || {};
    const payload = {
      ...formValues,
      billing:
        formValues.plan === "free" ? undefined : { ...billingWithoutGateway },
    };
    try {
      await signup(payload);
      await logout();
      toast.success("Thanks for your interest");
      navigate("/");
    } catch (error) {
      toast.error("Please try again");
    }
  };

  return (
    <section>
      <Container maxWidth="sm">
        <Box pt={8} pb={10}>
          <Box mb={6} textAlign="center">
            <Typography variant="h4" component="h2" gutterBottom>
              Sign Up
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" paragraph>
              Create your account
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      type="email"
                      variant="outlined"
                      required
                      fullWidth
                      onChange={onChange}
                      value={value}
                      id="email"
                      label={"Email"}
                      placeholder="Email address"
                      autoComplete="email"
                    />
                  )}
                  {...register("email", {
                    required: true,
                    pattern:
                      /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                  })}
                  ref={null}
                />
                {errors.email && (
                  <RedSpan>Please enter a valid email address</RedSpan>
                )}
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      type="password"
                      variant="outlined"
                      required
                      fullWidth
                      onChange={onChange}
                      value={value}
                      id="password"
                      label={"Password"}
                      placeholder="Password"
                      autoComplete="new-password"
                    />
                  )}
                  {...register("password", {
                    required: true,
                  })}
                  ref={null}
                />
                {errors.password && <RedSpan>Please enter a password</RedSpan>}
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="plan"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      select
                      variant="outlined"
                      required
                      fullWidth
                      onChange={onChange}
                      value={value}
                      id="plan"
                      label={"Plan"}
                    >
                      <MenuItem value="free">Free</MenuItem>
                      {!bypassFreemium && (
                        <MenuItem value="pro_monthly">Pro Monthly</MenuItem>
                      )}
                      {!bypassFreemium && (
                        <MenuItem value="pro_annual">Pro Annual</MenuItem>
                      )}
                    </TextField>
                  )}
                  {...register("plan", { required: true })}
                  ref={null}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="displayName"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      type="text"
                      variant="outlined"
                      fullWidth
                      onChange={onChange}
                      value={value}
                      id="displayName"
                      label={"Display Name (optional)"}
                      placeholder="Display Name"
                    />
                  )}
                  {...register("displayName")}
                  ref={null}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" component="h3">
                  Billing Information
                </Typography>
              </Grid>

              {isPaidPlan && (
                <Grid item xs={12}>
                    <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    bgcolor="#f6f6f6"
                    p={2}
                    borderRadius={1}
                  >
                    {trialBillingMessage && (
                      <Typography variant="body2" color="textSecondary">
                        {trialBillingMessage}
                      </Typography>
                    )}
                    {false && (
                      <>
                        <Typography variant="subtitle1" component="h4">
                          Tokenize your card with Helcim PayJS
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          We initialize a Helcim checkout session for paid plans
                          so your card data is tokenized before submission.
                          Click the button below once the checkout token is
                          ready.
                        </Typography>
                      </>
                    )}
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {false && (
                        <>
                          <Button
                            variant="outlined"
                            color="primary"
                            disabled={helcimInitStatus === "loading"}
                            onClick={() => {
                              setHelcimInitStatus("idle");
                              setHelcimTokenMessage("");
                              setHelcimInitError("");
                              setHelcimCheckoutToken("");
                              toast.info("Refreshing Helcim checkout token...");
                              refreshHelcimCheckoutToken(
                                shouldChargeForPlan
                              ).catch(() => {});
                            }}
                          >
                            Refresh Helcim token
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            disabled={helcimScriptStatus === "loading"}
                            onClick={() => {
                              setHelcimScriptError("");
                              loadHelcimScript().catch((error) => {
                                setHelcimScriptStatus("error");
                                setHelcimScriptError(error.message);
                              });
                            }}
                          >
                            Reload HelcimPay.js
                          </Button>
                        </>
                      )}
                      {isTrialPlan && (
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={helcimActionDisabled}
                          onClick={() => handleHelcimFlow(false)}
                        >
                          {isConfirmingHelcim
                            ? "Saving billing..."
                            : "Save payment method"}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={subscribeDisabled}
                        onClick={() => handleHelcimFlow(shouldChargeForPlan)}
                      >
                        {isConfirmingHelcim ? "Subscribing..." : "Subscribe"}
                      </Button>
                    </Box>
                    {false && (
                      <>
                        <Typography variant="caption" color="textSecondary">
                          Status: {helcimInitStatus}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          HelcimPay.js: {helcimScriptStatus}
                        </Typography>
                        {helcimCheckoutToken && (
                          <Typography variant="caption" color="textSecondary">
                            Checkout Token: {helcimCheckoutToken}
                          </Typography>
                        )}
                        {helcimScriptStatus === "loading" && (
                          <Typography variant="caption" color="textSecondary">
                            Waiting for HelcimPay.js to load from {helcimPayJsUrl}
                          </Typography>
                        )}
                        {helcimScriptError && (
                          <RedSpan>{helcimScriptError}</RedSpan>
                        )}
                        {helcimInitError && <RedSpan>{helcimInitError}</RedSpan>}
                        {helcimTokenMessage && (
                          <Typography variant="body2" color="primary">
                            {helcimTokenMessage}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  required={isPaidPlan}
                  fullWidth
                  label="Full Name"
                  placeholder="Billing contact name"
                  {...register("billing.fullName", {
                    required:
                      isPaidPlan && "Full name is required for paid plans",
                  })}
                />
                {errors?.billing?.fullName && (
                  <RedSpan>{errors.billing.fullName.message}</RedSpan>
                )}
              </Grid>

              <input
                type="hidden"
                {...register("billing.helcim.customerId", {
                  required: isPaidPlan && "Customer ID is required for paid plans",
                })}
              />
              <input
                type="hidden"
                {...register("billing.helcim.defaultCardToken", {
                  required:
                    isPaidPlan && "Default card token is required for paid plans",
                })}
              />
              <input
                type="hidden"
                {...register("billing.cardToken", {
                  required: isPaidPlan && "Card token is required for paid plans",
                })}
              />

              <Grid item xs={12}>
                <Typography variant="h6" component="h3">
                  Billing Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  required={isPaidPlan}
                  fullWidth
                  label="Address Line 1"
                  placeholder="Street address"
                  {...register("billing.address.line1", {
                    required:
                      isPaidPlan && "Address is required for paid plans",
                  })}
                />
                {errors?.billing?.address?.line1 && (
                  <RedSpan>{errors.billing.address.line1.message}</RedSpan>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  required={isPaidPlan}
                  fullWidth
                  label="City"
                  placeholder="City"
                  {...register("billing.address.city", {
                    required: isPaidPlan && "City is required for paid plans",
                  })}
                />
                {errors?.billing?.address?.city && (
                  <RedSpan>{errors.billing.address.city.message}</RedSpan>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  required={isPaidPlan}
                  fullWidth
                  label="State/Province"
                  placeholder="State or province"
                  {...register("billing.address.state", {
                    required:
                      isPaidPlan &&
                      "State or province is required for paid plans",
                  })}
                />
                {errors?.billing?.address?.state && (
                  <RedSpan>{errors.billing.address.state.message}</RedSpan>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  required={isPaidPlan}
                  fullWidth
                  label="Postal Code"
                  placeholder="Postal code"
                  {...register("billing.address.postalCode", {
                    required:
                      isPaidPlan && "Postal code is required for paid plans",
                  })}
                />
                {errors?.billing?.address?.postalCode && (
                  <RedSpan>{errors.billing.address.postalCode.message}</RedSpan>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  defaultValue=""
                  variant="outlined"
                  required={isPaidPlan}
                  fullWidth
                  label="Country"
                  autoComplete="country"
                  placeholder="Country code (e.g., US)"
                  {...register("billing.address.country", {
                    required: isPaidPlan && "Country is required for paid plans",
                    setValueAs: normalizeCountryIso2,
                    validate: (v) =>
                      v === "US" || v === "CA" || "Country must be US or CA",
                  })}
                >
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                </TextField>
                {errors?.billing?.address?.country && (
                  <RedSpan>{errors.billing.address.country.message}</RedSpan>
                )}
              </Grid>
            </Grid>
            <Box my={2}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={!isValid}
              >
                Sign Up
              </Button>
            </Box>
          </form>
        </Box>
      </Container>
    </section>
  );
};

export default Signup;
